import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Input from "@/components/ui/input";
import { useInputWithValidation } from "@/hooks/useInputWithValidation";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import { getValidationForDisplayType } from "@/utils/entityFieldsUtils/shared";
import { fromPercentInput, toPercentInput } from "@/utils/numbers";
import { cn } from "@/utils/utils";
import {
  getPlaceholder,
  processValue,
  FIELD_CLASSNAME,
  HALF_WRAPPER_CLASSNAME
} from "../shared";
import { asString, ControlProps } from "../types";

export const TextControl: React.FC<ControlProps> = ({
  fieldType,
  config,
  value,
  onChange,
  name,
  showNumberHints
}) => {
  const { t } = useTranslation();
  const isPercentage = fieldType === FieldDisplayType.PERCENTAGE;
  const isMonetary = fieldType === FieldDisplayType.CURRENCY;

  // Percentages are stored as fractions (0.3 = 30%) but edited in 1–100 space.
  const initialValue = isPercentage
    ? (toPercentInput(
        value as number | string | null | undefined
      )?.toString() ?? "")
    : asString(value);

  const {
    value: inputValue,
    error,
    handleChange,
    handleBlur,
    getMonetaryInputValue,
    currencySign
  } = useInputWithValidation({
    initialValue,
    validation: getValidationForDisplayType(fieldType, t, config),
    isMonetary,
    isPretty: true,
    // Leaves as the clamped raw string; percentages convert back to a fraction.
    onSave: (rawValue) => {
      const processed = processValue(rawValue, fieldType, config);
      onChange(
        isPercentage && processed !== ""
          ? (fromPercentInput(processed) ?? processed)
          : processed
      );
    }
  });

  return (
    <div
      className={
        fieldType === FieldDisplayType.TEXT ? "" : HALF_WRAPPER_CLASSNAME
      }
    >
      <Input
        type="text"
        value={getMonetaryInputValue(inputValue)}
        onChange={(event) => handleChange(event.target.value)}
        onBlur={handleBlur}
        placeholder={getPlaceholder(fieldType, t)}
        className={cn("w-full", FIELD_CLASSNAME, error && "border-red-400")}
        data-test={`${name}-input`}
        leadingIcon={currencySign}
      />
      {error ? (
        <Text variant="smBody" className="text-primary mt-1">
          {error}
        </Text>
      ) : null}
      {showNumberHints && config?.unit ? (
        <Text variant="smBodySecondary" className="mt-1">
          {t("questionnaire.labels.unit")}: {config.unit}
        </Text>
      ) : null}
      {showNumberHints && (config?.minValue || config?.maxValue) ? (
        <Text variant="smBodySecondary" className="mt-1">
          {t("questionnaire.labels.range")}: {config.minValue || "0"} -{" "}
          {config.maxValue || "∞"}
        </Text>
      ) : null}
    </div>
  );
};
